import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';

/**
 * 模型加载器模块
 * 集成 GLTFLoader + Draco 压缩解压，支持主流数字人 GLB/GLTF 模型加载
 * 包含进度回调、错误捕获、资源释放等工程化能力
 */
export class ModelLoader {
  /** @type {GLTFLoader} */
  #loader;

  /** Draco 解码器 CDN 路径（可替换为本地路径） */
  #dracoPath = 'https://cdn.jsdelivr.net/npm/three@0.172.0/examples/jsm/libs/draco/';

  constructor() {
    this.#loader = new GLTFLoader();
    this.#setupDraco();
  }

  /** 配置 Draco 解码器，用于解压压缩过的 GLB 模型 */
  #setupDraco() {
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath(this.#dracoPath);
    this.#loader.setDRACOLoader(dracoLoader);
  }

  /**
   * 加载数字人模型
   * @param {string} url - 模型文件路径（支持 .glb / .gltf）
   * @param {object} [callbacks] - 回调函数
   * @param {(progress: { loaded: number; total: number }) => void} [callbacks.onProgress] - 加载进度回调
   * @param {(error: Error) => void} [callbacks.onError] - 加载失败回调
   * @returns {Promise<{ scene: THREE.Group, animations: THREE.AnimationClip[] }>} 模型场景 + 动画剪辑
   */
  load(url, callbacks = {}) {
    const { onProgress, onError } = callbacks;

    return new Promise((resolve, reject) => {
      this.#loader.load(
        url,
        // onLoad
        (gltf) => {
          resolve({
            scene: gltf.scene,
            animations: gltf.animations || [],
          });
        },
        // onProgress
        onProgress
          ? (event) => {
              if (event.lengthComputable) {
                onProgress({
                  loaded: event.loaded,
                  total: event.total,
                  percent: Math.round((event.loaded / event.total) * 100),
                });
              }
            }
          : undefined,
        // onError
        (error) => {
          const err = error instanceof Error ? error : new Error(String(error));
          onError?.(err);
          reject(err);
        }
      );
    });
  }

  /** 获取底层 GLTFLoader 实例（高级用法） */
  getLoader() {
    return this.#loader;
  }
}
